import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, Fragment } from "react";
import { noop, without } from "lodash";
import { colors, s8, s16 } from "@ardoq/design-tokens";
import styled from "styled-components";
import { isTruthy } from "@ardoq/common-helpers";
import { DRAG_BUTTON_CLASS_NAME, DragButton } from "@ardoq/icons";
const BAR_COLOR = colors.borderFocus;
const DropPlaceholderBar = styled.div`
  position: relative;
  border-bottom: 3px solid ${BAR_COLOR};
  border-radius: 5px;
  margin: ${s8} ${s16};

  &::before {
    position: absolute;
    left: 0;
    top: -3px;
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: ${BAR_COLOR};
    content: ' ';
  }
`;
const defaultDragAndDropState = {
  draggedCardId: null,
  draggedOverCardId: null,
  position: null,
  setDraggedCardId: noop,
  setDraggedOverCard: noop,
  resetState: noop
};
const DragAndDropContext = createContext({
  ...defaultDragAndDropState
});
const useDragAndDropContext = () => useContext(DragAndDropContext);
const getNewOrder = (currentOrder, { draggedCardId, draggedOverCardId, position }) => {
  if (!draggedOverCardId || !draggedCardId || draggedCardId === draggedOverCardId) {
    return currentOrder;
  }
  const withoutDragged = without(currentOrder, draggedCardId);
  const index = withoutDragged.indexOf(draggedOverCardId) + (position === "below" ? 1 : 0);
  const newOrder = [...withoutDragged];
  newOrder.splice(index, 0, draggedCardId);
  return newOrder;
};
const DragAndDropSectionsProvider = ({
  onDrop,
  children
}) => {
  const currentOrder = children.map(({ props }) => props.dragAndDropCardId).filter(isTruthy);
  const [draggedCardId, setDraggedCardId] = useState(null);
  const [draggedOverCardId, setDraggedOverCardId] = useState(
    null
  );
  const [position, setPosition] = useState(null);
  const setDraggedOverCard = (newDragOverCardId, newPosition) => {
    if (!draggedCardId || !currentOrder.includes(draggedCardId)) return;
    setDraggedOverCardId(newDragOverCardId);
    setPosition(newPosition);
  };
  const resetState = () => {
    setDraggedCardId(null);
    setDraggedOverCardId(null);
    setPosition(null);
  };
  const contextProps = {
    draggedCardId,
    draggedOverCardId,
    position,
    resetState,
    setDraggedCardId,
    setDraggedOverCard
  };
  useEffect(() => {
    const handleDragEnd = () => {
      const newOrder = getNewOrder(currentOrder, contextProps);
      onDrop == null ? void 0 : onDrop(newOrder, contextProps);
      children.forEach(({ props }) => {
        const { dragAndDropCardId, onOrderChange } = props;
        if (!dragAndDropCardId) return;
        const currentIndex = currentOrder.indexOf(dragAndDropCardId);
        const newIndex = newOrder.indexOf(dragAndDropCardId);
        if (currentIndex !== newIndex && onOrderChange) {
          onOrderChange(newIndex, currentIndex);
        }
      });
      resetState();
    };
    document.addEventListener("dragend", handleDragEnd, true);
    const preventDragOverEvent = (e) => e.preventDefault();
    document.addEventListener("dragover", preventDragOverEvent);
    return () => {
      document.removeEventListener("dragover", preventDragOverEvent);
      document.removeEventListener("dragend", handleDragEnd, true);
    };
  });
  return /* @__PURE__ */ jsx(DragAndDropContext.Provider, { value: contextProps, children: children.map((child, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
    child.props.dragAndDropCardId === draggedOverCardId && position === "above" && /* @__PURE__ */ jsx(DropPlaceholderBar, {}),
    child,
    child.props.dragAndDropCardId === draggedOverCardId && position === "below" && /* @__PURE__ */ jsx(DropPlaceholderBar, {})
  ] }, child.key ?? index)) });
};
const arrayMove = (array, fromIndex, toIndex) => {
  const arrayCopy = [...array];
  arrayCopy.splice(toIndex, 0, arrayCopy.splice(fromIndex, 1)[0]);
  return arrayCopy;
};
const isTargetDragHandleButton = (target) => target instanceof Element && target.classList.contains(DRAG_BUTTON_CLASS_NAME);
const Wrapper = styled.div`
  display: flex;
  ${DragButton} {
    line-height: 20px;
    display: block;
    visibility: ${(props) => props.$dragButtonAlwaysVisible ? "visible" : "hidden"};
  }
  &:hover {
    ${DragButton} {
      visibility: visible;
    }
  }
`;
const DragAndDropPositioningWrapper = styled.div`
  flex-grow: 1;
  order: ${(props) => props.$dragButtonPosition === "right" ? 0 : 1};
`;
const DragAndDropCardWrapper = ({
  dragAndDropCardId,
  children,
  className,
  dragButtonAlwaysVisible = false,
  dragButtonPosition = "right",
  dataTestId,
  ...props
}) => {
  const { setDraggedCardId, setDraggedOverCard, resetState } = useDragAndDropContext();
  return /* @__PURE__ */ jsxs(
    Wrapper,
    {
      "data-test-id": dataTestId,
      className,
      $dragButtonAlwaysVisible: dragButtonAlwaysVisible,
      onDragStart: (event) => {
        if (!isTargetDragHandleButton(event.target)) {
          return;
        }
        event.dataTransfer.setDragImage(event.currentTarget, 0, 0);
        setDraggedCardId(dragAndDropCardId || null);
      },
      onDragOver: (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const middleOfRect = rect.top + rect.height / 2;
        const position = middleOfRect > e.clientY ? "above" : "below";
        setDraggedOverCard(dragAndDropCardId || null, position);
      },
      onDragEnd: () => {
        resetState();
      },
      ...props,
      children: [
        /* @__PURE__ */ jsx(DragAndDropPositioningWrapper, { $dragButtonPosition: dragButtonPosition, children }),
        Boolean(dragAndDropCardId) && /* @__PURE__ */ jsx(
          DragButton,
          {
            draggable: Boolean(dragAndDropCardId),
            "data-test-id": `${dataTestId}-drag-button`
          }
        )
      ]
    }
  );
};
export {
  DragAndDropCardWrapper,
  DragAndDropContext,
  DragAndDropPositioningWrapper,
  DragAndDropSectionsProvider,
  arrayMove,
  isTargetDragHandleButton,
  useDragAndDropContext
};
//# sourceMappingURL=index.js.map
